var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DialogueBackgroundMode } from '@common/contracts';
import { getScenarioById, updateScenario } from '../api/scenarios';
import { getDialogueById, updateDialogue } from '../api/dialogues';
import { EventBus } from '../utils/EventBus';
/**
 * Single choke point for persisting a scenario.
 *
 * Every component must save scenarios through this function: it owns the
 * EventBus protocol (attempt → updated | failed), so listeners can never
 * receive an "updated" broadcast for data that is not in the database.
 *
 * Returns the saved scenario, or null when the save failed (the failure has
 * already been broadcast and logged).
 */
export function persistScenario(levelId, scenario, system) {
    return __awaiter(this, void 0, void 0, function* () {
        EventBus.emit('scenarioUpdateAttempt', { scenarioId: scenario.id });
        EventBus.emit('systemAttempt', { scenarioId: scenario.id, system });
        try {
            const saved = yield updateScenario(levelId, scenario.id, scenario);
            EventBus.emit('scenarioUpdated', saved);
            EventBus.emit('systemSuccess', { scenarioId: scenario.id, system });
            return saved;
        }
        catch (err) {
            console.error(`[${system}] Failed to save scenario ${scenario.id}`, err);
            EventBus.emit('scenarioUpdateFailed', { scenarioId: scenario.id, error: String(err) });
            EventBus.emit('systemError', { scenarioId: scenario.id, system, error: String(err) });
            return null;
        }
    });
}
/**
 * Sets (or clears, when imageUrl is undefined) the NPC-node background image
 * in the given linked scenarios, and mirrors the change into their dialogues
 * that run in Map background mode.
 *
 * Used when a map node's art changes: uploaded, replaced, removed, or when a
 * scenario is first linked to a node that already has art.
 */
export function propagateNodeBackground(levelId, scenarioIds, imageUrl, system) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const scenarioId of scenarioIds) {
            try {
                const scenario = yield getScenarioById(levelId, scenarioId);
                if (!scenario)
                    continue;
                let changed = false;
                scenario.nodes.forEach(flowNode => {
                    var _a;
                    if (((_a = flowNode === null || flowNode === void 0 ? void 0 : flowNode.data) === null || _a === void 0 ? void 0 : _a.type) === 'npc' && flowNode.data.backgroundImage !== imageUrl) {
                        flowNode.data.backgroundImage = imageUrl;
                        flowNode.data._forceUpdate = Date.now();
                        changed = true;
                    }
                });
                if (!changed)
                    continue;
                yield persistScenario(levelId, scenario, system);
                yield propagateBackgroundToMapDialogues(levelId, scenario, imageUrl);
            }
            catch (err) {
                console.error(`[${system}] Failed to propagate node background to scenario ${scenarioId}`, err);
            }
        }
    });
}
/**
 * Mirrors a background change into every dialogue referenced by the
 * scenario's NPC nodes, but only for dialogues in Map background mode —
 * their backdrop is defined by the map cell the scenario is linked to.
 */
function propagateBackgroundToMapDialogues(levelId, scenario, imageUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const dialogueIds = Array.from(new Set(scenario.nodes
            .filter(n => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.type) === 'npc'; })
            .map(n => n.data.dialogueId)
            .filter((id) => Boolean(id))));
        for (const dialogueId of dialogueIds) {
            try {
                const dialogue = yield getDialogueById(levelId, dialogueId);
                if (!dialogue || dialogue.backgroundMode !== DialogueBackgroundMode.Map)
                    continue;
                let changed = false;
                dialogue.nodes.forEach(node => {
                    if (node.backgroundImage !== imageUrl) {
                        node.backgroundImage = imageUrl;
                        changed = true;
                    }
                });
                if (changed) {
                    const updated = yield updateDialogue(levelId, dialogueId, dialogue);
                    EventBus.emit('dialogueUpdated', updated);
                }
            }
            catch (err) {
                console.warn('Failed to update dialogue background', dialogueId, err);
            }
        }
    });
}
